<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="list-projects-wrapper content-wrapper">
    <b-loading :is-full-page="false" :active="loading" />
    <div class="box error" v-if="error">
      <h2> {{ $t('error') }} </h2>
      <p>{{ $t('unexpected-error-info-message') }}</p>
    </div>
    <div v-else-if="!loading" class="panel">
      <div class="panel-heading">
        <strong class="panel-heading-title">
          {{ $t('case-management') }}
        </strong>
      </div>
      <div class="panel-block">
        <div class="panel-heading-buttons" v-if="!$keycloak.hasTemporaryToken">
          <div class="buttons has-addons">
            <button class="button" :class="{ 'is-primary': all }" @click="all = true; revision++">
              {{ $t('all-cases') }}
            </button>
            <button class="button" :class="{ 'is-primary': !all }" @click="all = false; revision++">
              {{ $t('my-cases') }}
            </button>
          </div>
          <b-input class="search-input" v-model="searchString" :placeholder="$t('search-cases')" type="search"
            icon="search" />
          <button class="button" @click="toggleFilterDisplay()">
            <span class="icon">
              <i class="fas fa-filter"></i>
            </span>
            <span>
              {{ filtersOpened ? $t('button-hide-filters') : $t('button-show-filters') }}
            </span>
            <span v-if="nbActiveFilters" class="nb-active-filters">
              {{ nbActiveFilters }}
            </span>
          </button>
          <button class="button is-link" @click="creationModal = true">
            <span class="icon">
              <i class="fas fa-plus"></i>
            </span>
            <span>{{ $t('new-case') }}</span>
          </button>
          <button v-if="checkedProjects.length > 0" class="button is-info" @click="bulkShare">
            <span class="icon is-small">
              <i class="fas fa-share-alt"></i>
            </span>
            <span>{{ $t('bulk-share') }}</span>
          </button>
          <button v-if="checkedProjects.length > 0" class="button is-warning" @click="bulkAssign">
            <span class="icon is-small">
              <i class="fas fa-user-tag"></i>
            </span>
            <span>{{ $t('bulk-assign') }}</span>
          </button>
          <button v-if="checkedProjects.length > 0" class="button is-primary" @click="bulkRunAI">
            <span class="icon is-small">
              <i class="fas fa-robot"></i>
            </span>
            <span>{{ $t('button-run-ai') }}</span>
          </button>
          <!-- <button v-if="checkedProjects.length > 0" class="button is-danger" @click="deleteSelectedProjects">
            <span class="icon is-small">
              <i class="fas fa-trash"></i>
            </span>
            <span>Delete</span>
          </button> -->
        </div>
        <b-collapse :open="filtersOpened">
          <div class="filters">
            <div class="columns">
              <!-- <div class="column filter">
                <div class="filter-label">
                  {{ $t('my-role') }}
                </div>
                <div class="filter-body">
                  <cytomine-multiselect v-model="selectedRoles" :options="availableRoles" multiple
                    :searchable="false" />
                </div>
              </div> -->
              <div class="column filter">
                <div class="filter-label">
                  {{ $t('patient-id') }}
                </div>
                <div class="filter-body">
                  <b-input v-model="patientIdFilter" :placeholder="$t('filter-by-patient-id')" />
                </div>
              </div>

              <div class="column filter">
                <div class="filter-label">
                  {{ $t('patient-name') }}
                </div>
                <div class="filter-body">
                  <b-input v-model="patientNameFilter" :placeholder="$t('filter-by-patient-name')" />
                </div>
              </div>

              <div class="column filter">
                <div class="filter-label">
                  {{ $t('review') }}
                </div>
                <div class="filter-body">
                  <cytomine-multiselect v-model="selectedStatuses" :options="availableStatuses" label="name"
                    track-by="id" :multiple="true" :allPlaceholder="$t('all-statuses')" />
                </div>
              </div>
            </div>

            <div class="columns">
              <div class="column filter">
                <div class="filter-label">
                  {{ $t('accession-id') }}
                </div>
                <div class="filter-body">
                  <b-input v-model="accessionIdFilter" :placeholder="$t('filter-by-accession-id')" />
                </div>
              </div>

              <div class="column filter">
                <div class="filter-label">
                  {{ $t('medical-record-number') }}
                </div>
                <div class="filter-body">
                  <b-input v-model="medicalRecordNumberFilter" :placeholder="$t('filter-by-mrn')" />
                </div>
              </div>

              <div class="column filter">
                <div class="filter-label">
                  {{ $t('tissue') }}
                </div>
                <div class="filter-body">
                  <b-input v-model="tissueFilter" :placeholder="$t('filter-by-tissue')" />
                </div>
              </div>
              <div class="column filter">
                <div class="filter-label">
                  {{ $t('specimen') }}
                </div>
                <div class="filter-body">
                  <b-input v-model="specimenFilter" :placeholder="$t('filter-by-specimen')" />
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
        <cytomine-table :collection="projectCollection" :is-empty="nbEmptyFilters > 0" class="table-projects"
          :currentPage.sync="currentPage" :perPage.sync="perPage" :openedDetailed.sync="openedDetails"
          :sort.sync="sortField" :order.sync="sortOrder" :revision="revision" :checkable="true"
          :checked-rows.sync="checkedProjects" @page-loaded="hydrateProjectRows">
          <template #default="{ row: project }">
            <!-- <b-table-column field="currentUserRole" label="" centered width="1" sortable>
              <icon-project-member-role :is-manager="project.currentUserRoles.admin"
                :is-representative="project.currentUserRoles.representative" />
            </b-table-column> -->

            <!-- <b-table-column field="patientName" label="Name" centered sortable width="150">
              {{ project.patientName }}
            </b-table-column> -->

            <!-- <b-table-column field="patientAge" label="Age" centered sortable width="50">
              {{ project.patientAge }}
            </b-table-column>

            <b-table-column field="patientSex" :label="$t('gender')" centered sortable width="50">
              {{ project.patientSex }}
            </b-table-column> -->

            <b-table-column label="Thumbnail" centered width="96">
              <router-link v-if="projectThumbnail(project)" :to="projectViewerLink(project)" class="case-thumbnail-link">
                <image-thumbnail :image="projectThumbnail(project)" :size="96"
                  :key="`${projectThumbnail(project).id}-case-thumb-96`"
                  :extra-parameters="{Authorization: 'Bearer ' + shortTermToken}" />
              </router-link>
              <span v-else-if="projectThumbnailLoading(project)" class="icon case-thumbnail-placeholder">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <span v-else class="icon case-thumbnail-placeholder">
                <i class="far fa-image"></i>
              </span>
            </b-table-column>

            <b-table-column field="name" label="Case ID" sortable width="180">
              <span class="case-id">{{ projectCaseId(project) }}</span>
            </b-table-column>

            <b-table-column field="tissue" :label="$t('tissue')" centered sortable width="120">
              {{ project.tissue || '-' }}
            </b-table-column>

            <b-table-column field="stain" :label="$t('stain')" centered sortable width="120">
              {{ project.stain || '-' }}
            </b-table-column>

            <b-table-column label="AI" centered width="90">
              <b-tooltip v-if="projectAIStatus(project)" :label="getAiStatusHint(projectAIStatus(project))"
                position="is-top" type="is-dark">
                <div class="ai-status">
                  <span class="icon is-small has-text-grey-light">
                    <i class="fas fa-robot"></i>
                  </span>
                  <span class="icon is-small has-text-warning" v-if="projectAIStatus(project) === 'Process'">
                    <i class="fas fa-spinner fa-spin"></i>
                  </span>
                  <span class="icon is-small has-text-danger" v-else-if="projectAIStatus(project) === 'Error'">
                    <i class="fas fa-exclamation-circle"></i>
                  </span>
                  <span class="icon is-small has-text-success" v-else-if="projectAIStatus(project) === 'Ready'">
                    <i class="fas fa-check-circle"></i>
                  </span>
                </div>
              </b-tooltip>
              <span v-else class="has-text-grey">-</span>
            </b-table-column>

            <b-table-column label="QC" centered width="100">
              <span class="case-status" :class="qcStatusClass(project)">
                {{ formatQcStatus(project) }}
              </span>
            </b-table-column>

            <b-table-column field="status" :label="$t('review')" centered sortable width="120">
              <span class="case-status" :class="statusClass(project.status, project.statusCode)">
                {{ formatStatus(project.status, project.statusCode) }}
              </span>
            </b-table-column>


            <b-table-column :label="$t('actions')" centered width="200">
              <div class="buttons">
                <button v-if="!$keycloak.hasTemporaryToken" class="button" @click="openAddImageModal(project)">
                  <span class="icon">
                    <i class="fas fa-plus"></i>
                  </span>
                  <!-- <span>{{ $t('button-add-image') }}</span> -->
                </button>
                <button class="button" @click="openProject(project)">
                  <span class="icon">
                    <i class="fas fa-eye"></i>
                  </span>
                  <!-- <span>Open viewer</span> -->
                </button>
                <button v-if="!$keycloak.hasTemporaryToken" class="button" @click="openShareModal(project)">
                  <span class="icon">
                    <i class="fas fa-share-alt"></i>
                  </span>
                  <!-- <span>{{ $t('button-share') }}</span> -->
                </button>
                <button v-if="!$keycloak.hasTemporaryToken" class="button" @click="runAIOnProject(project)">
                  <span class="icon">
                    <i class="fas fa-robot"></i>
                  </span>
                  <!-- <span>Run AI</span> -->
                </button>
              </div>
            </b-table-column>
          </template>

          <template #detail="{ row: project }">
            <project-details :project="project" :excluded-properties="excludedProperties" editable
              @update="updateProject()" @delete="deleteProject(project)" />
          </template>

          <template #empty>
            <div class="content has-text-grey has-text-centered">
              <p>{{ $t('no-project') }}</p>
            </div>
          </template>
        </cytomine-table>
      </div>
    </div>

    <add-project-modal :active.sync="creationModal" :ontologies="ontologies" />
    <add-image-modal :active.sync="addImageModal" :project="selectedProject" @addImage="updateProject()" />
    <share-project-modal :active.sync="shareProjectModal" :project="selectedProject" :projects="selectedProjects" />

    <!-- Bulk Action Modal -->
    <div v-if="bulkActionModal" class="modal is-active">
      <div class="modal-background" @click="bulkActionModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('batch-actions') }}</p>
          <button class="delete" aria-label="close" @click="bulkActionModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('batch-actions-help') }}</p>
          <!-- 在这里可以添加批量操作选项 -->
          <div class="bulk-actions">
            <button class="button is-info" @click="bulkShare">
              <span class="icon is-small">
                <i class="fas fa-share-alt"></i>
              </span>
              <span>{{ $t('button-share') }}</span>
            </button>
            <button class="button is-warning" @click="bulkAssign">
              <span class="icon is-small">
                <i class="fas fa-user-tag"></i>
              </span>
              <span>{{ $t('assign') }}</span>
            </button>
            <button class="button is-primary" @click="bulkRunAI">
              <span class="icon is-small">
                <i class="fas fa-robot"></i>
              </span>
              <span>{{ $t('button-run-ai') }}</span>
            </button>
            <button class="button is-danger" @click="deleteSelectedProjects">
              <span class="icon is-small">
                <i class="fas fa-trash"></i>
              </span>
              <span>{{ $t('button-delete') }}</span>
            </button>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="bulkActionModal = false">{{ $t('button-close') }}</button>
        </footer>
      </div>
    </div>

    <!-- Bulk Assign Modal -->
    <div v-if="assignToModal" class="modal is-active">
      <div class="modal-background" @click="assignToModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('assign-users') }}</p>
          <button class="delete" aria-label="close" @click="assignToModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('assign-users-selected-projects', { count: checkedProjects.length }) }}</p>

          <div class="field mt-4">
            <div class="control">
              <b-dropdown v-model="bulkRepresentatives" multiple>
                <template #trigger="{ active }">
                  <b-button type="is-text" :icon-right="active ? 'angle-up' : 'angle-down'"
                    expanded>
                    {{ bulkRepresentatives.length > 0 ? $t('selected-count', { count: bulkRepresentatives.length }) : $t('select-users') }}
                  </b-button>
                </template>

                <b-dropdown-item v-for="user in allUsers" :key="user.id" :value="user.id">
                  <span>{{ user.name }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="assignToModal = false">{{ $t('button-cancel') }}</button>
          <button class="button is-primary" @click="confirmBulkAssign" :disabled="bulkRepresentatives.length === 0">
            {{ $t('button-confirm') }}
          </button>
        </footer>
      </div>
    </div>

    <!-- AI Runner Selection Modal -->
    <div v-if="aiRunnerSelectionModal" class="modal is-active">
      <div class="modal-background" @click="aiRunnerSelectionModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('select-ai-models') }}</p>
          <button class="delete" aria-label="close" @click="aiRunnerSelectionModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('select-ai-model-for-selected-cases') }}</p>

          <div class="field">
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="selectedAIRunner">
                  <option value="">{{ $t('please-select') }}</option>
                  <option v-for="runner in aiRunners" :key="runner.id" :value="runner">
                    {{ runner.name }} ({{ runner.runnerName }})
                  </option>
                </select>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="aiRunnerSelectionModal = false">{{ $t('button-cancel') }}</button>
          <button class="button is-primary" :disabled="!selectedAIRunner" @click="confirmRunAI">
            {{ $t('button-confirm') }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Single Project AI Runner Selection Modal -->
    <div v-if="singleAIRunnerSelectionModal" class="modal is-active">
      <div class="modal-background" @click="singleAIRunnerSelectionModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('select-ai-models') }}</p>
          <button class="delete" aria-label="close" @click="singleAIRunnerSelectionModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('select-ai-model-for-this-case') }}</p>

          <div class="field">
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="selectedSingleAIRunner">
                  <option value="">{{ $t('please-select') }}</option>
                  <option v-for="runner in aiRunners" :key="runner.id" :value="runner">
                    {{ runner.name }} ({{ runner.runnerName }})
                  </option>
                </select>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="singleAIRunnerSelectionModal = false">{{ $t('button-cancel') }}</button>
          <button class="button is-primary" :disabled="!selectedSingleAIRunner" @click="confirmSingleRunAI">
            {{ $t('button-confirm') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import CytomineTable from '@/components/utils/CytomineTable';
import CytomineMultiselect from '@/components/form/CytomineMultiselect';
import CytomineSlider from '@/components/form/CytomineSlider';
import ProjectDetails from './ProjectDetails';
import AddProjectModal from './AddProjectModal';
import ShareProjectModal from './ShareProjectModal';

import { get, sync, syncBoundsFilter, syncMultiselectFilter } from '@/utils/store-helpers';

import Cytomine from '@/api/cytomine';
import { ImageInstanceCollection, ProjectCollection, OntologyCollection, TagCollection, AIRunner, AIAlgorithmJob, UserCollection, Project, ProjectRepresentative, ProjectRepresentativeCollection } from '@/api';
import IconProjectMemberRole from '@/components/icons/IconProjectMemberRole';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import AddImageModal from '@/components/image/AddImageModal.vue';
import {
  formatProjectQcStatus,
  formatProjectReviewStatus,
  getProjectCaseId,
  getProjectQcStatus,
  latestProjectAIStatus,
  projectReviewStatusOptions
} from '@/components/project/project-list-formatters';
export default {
  name: 'list-projects',
  components: {
    IconProjectMemberRole,
    ImageThumbnail,
    CytomineTable,
    ProjectDetails,
    AddProjectModal,
    ShareProjectModal,
    AddImageModal,
    CytomineMultiselect,
    CytomineSlider
  },
  data() {
    return {
      loading: true,
      error: false,

      projects: [],
      ontologies: [],
      availableTags: [],
      allUsers: [],
      all: true,

      contributorLabel: this.$t('contributor'),
      managerLabel: this.$t('manager'),

      creationModal: false,
      addImageModal: false,
      shareProjectModal: false,
      assignToModal: false,
      bulkActionModal: false,
      aiRunnerSelectionModal: false,
      singleAIRunnerSelectionModal: false,
      selectedProject: null,
      selectedProjects: [], // 用于批量操作的项目数组
      projectToRunAI: null,

      checkedProjects: [],

      // 编辑代表用户的属性
      editingProjectId: null,
      projectRepresentatives: {}, // 存储每个项目的代表用户

      // 批量分配代表用户属性
      bulkRepresentatives: [],

      // AI Runners
      aiRunners: [],
      selectedAIRunner: null,
      selectedSingleAIRunner: null,
      projectThumbnails: {},
      projectThumbnailRequests: {},
      aiStatuses: {},
      aiStatusRequests: {},

      excludedProperties: [
        'name',
        'membersCount',
        'numberOfImages',
        'numberOfAnnotations',
        'numberOfReviewedAnnotations',
        'lastActivity',
        'patientId',
        'patientName',
        'patientAge',
        'accessionId',
        'status',
        'accessDate',
        'medicalRecordNumber',
        'tissue',
        'specimen'
      ],
      maxNbMembers: 10,
      maxNbImages: 10,
      maxNbUserAnnotations: 100,
      maxNbReviewedAnnotations: 100,

      // 新增的过滤字段
      patientIdFilter: '',
      patientNameFilter: '',
      accessionIdFilter: '',
      medicalRecordNumberFilter: '',
      tissueFilter: '',
      specimenFilter: '',
      selectedStatuses: [],

      revision: 0
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    shortTermToken: get('currentUser/shortTermToken'),

    searchString: sync('listProjects/searchString', { debounce: 500 }),
    filtersOpened: sync('listProjects/filtersOpened'),

    availableRoles() {
      return [this.contributorLabel, this.managerLabel];
    },
    availableOntologies() {
      return [{ id: 'null', name: this.$t('no-ontology') }, ...this.ontologies];
    },
    availableStatuses() {
      return projectReviewStatusOptions();
    },

    querySearchTags() {
      return this.$route.query.tags;
    },
    selectedOntologies: syncMultiselectFilter('listProjects', 'selectedOntologies', 'availableOntologies'),
    selectedRoles: syncMultiselectFilter('listProjects', 'selectedRoles', 'availableRoles'),
    selectedTags: syncMultiselectFilter('listProjects', 'selectedTags', 'availableTags'),
    boundsMembers: syncBoundsFilter('listProjects', 'boundsMembers', 'maxNbMembers'),
    boundsImages: syncBoundsFilter('listProjects', 'boundsImages', 'maxNbImages'),
    boundsUserAnnotations: syncBoundsFilter('listProjects', 'boundsUserAnnotations', 'maxNbUserAnnotations'),
    boundsReviewedAnnotations: syncBoundsFilter('listProjects', 'boundsReviewedAnnotations', 'maxNbReviewedAnnotations'),

    nbActiveFilters() {
      return this.$store.getters['listProjects/nbActiveFilters'];
    },
    nbEmptyFilters() {
      return this.$store.getters['listProjects/nbEmptyFilters'];
    },

    selectedOntologiesIds() {
      return this.selectedOntologies.map(ontology => ontology.id);
    },
    selectedStatusesIds() {
      return this.selectedStatuses.map(status => status.id);
    },

    boundsFilters() {
      return [
        { prop: 'numberOfImages', bounds: this.boundsImages, max: this.maxNbImages },
        { prop: 'membersCount', bounds: this.boundsMembers, max: this.maxNbMembers },
        { prop: 'numberOfAnnotations', bounds: this.boundsUserAnnotations, max: this.maxNbUserAnnotations },
        { prop: 'numberOfReviewedAnnotations', bounds: this.boundsReviewedAnnotations, max: this.maxNbReviewedAnnotations },
      ];
    },

    projectCollection() {
      let collection = new ProjectCollection({
        withMembersCount: true,
        withLastActivity: true,
        withCurrentUserRoles: true,
        withPatientId: true,
        withPatientName: true,
        withPatientAge: true,
        withCaseId: true,
        withAccessionId: true,
        withStatus: true,
        withAccessDate: true,
        withMedicalRecordNumber: true,
        withTissue: true,
        withSpecimen: true,
        withQcStatus: true,
        all: this.all
      });
      if (this.selectedOntologiesIds.length > 0 && this.selectedOntologiesIds.length < this.availableOntologies.length) {
        collection['ontology'] = {
          in: this.selectedOntologiesIds.join()
        };
      }
      if (this.selectedRoles.length > 0) {
        collection['currentUserRole'] = {
          in: this.selectedRoles.join().toLowerCase()
        };
      }
      if (this.searchString) {
        collection['patientName'] = {
          ilike: encodeURIComponent(this.searchString)
        };
      }
      if (this.selectedTags.length > 0 && this.selectedTags.length < this.availableTags.length) {
        collection['tag'] = {
          in: this.selectedTags.map(t => t.id).join()
        };
      }

      // 添加新的过滤条件
      if (this.patientIdFilter) {
        collection['patientId'] = {
          ilike: encodeURIComponent(this.patientIdFilter)
        };
      }
      if (this.patientNameFilter) {
        collection['patientName'] = {
          ilike: encodeURIComponent(this.patientNameFilter)
        };
      }
      if (this.accessionIdFilter) {
        collection['accessionId'] = {
          ilike: encodeURIComponent(this.accessionIdFilter)
        };
      }
      if (this.medicalRecordNumberFilter) {
        collection['medicalRecordNumber'] = {
          ilike: encodeURIComponent(this.medicalRecordNumberFilter)
        };
      }
      if (this.tissueFilter) {
        collection['tissue'] = {
          ilike: encodeURIComponent(this.tissueFilter)
        };
      }
      if (this.specimenFilter) {
        collection['specimen'] = {
          ilike: encodeURIComponent(this.specimenFilter)
        };
      }
      if (this.selectedStatusesIds.length > 0) {
        collection['status'] = {
          in: this.selectedStatusesIds.join()
        };
      }

      for (let { prop, bounds, max } of this.boundsFilters) {
        collection[prop] = {};
        if (bounds[1] !== max) {
          // if max bounds is the max possible value, do not set the filter in the request
          // so that if an event (ex: algo creates an annotation) happens between the bounds request and the query request
          // the image will not be skipped from the result
          collection[prop] = {
            lte: bounds[1]
          };
        }
        if (bounds[0] > 0) {
          collection[prop]['gte'] = bounds[0];
        }
      }
      return collection;
    },
    currentPage: sync('listProjects/currentPage'),
    perPage: sync('listProjects/perPage'),
    sortField: sync('listProjects/sortField'),
    sortOrder: sync('listProjects/sortOrder'),
    openedDetails: sync('listProjects/openedDetails')
  },
  watch: {
    revision() {
      this.fetchOntologies();
      this.fetchMaxFilters();
    },
    querySearchTags(values) {
      if (values) {
        this.selectedTags = [];
        let queriedTags = this.availableTags.filter(tag => values.split(',').includes(tag.name));
        if (queriedTags) {
          this.selectedTags = queriedTags;
        }
      }
    },
    patientIdFilter() {
      this.revision++;
    },
    patientNameFilter() {
      this.revision++;
    },
    accessionIdFilter() {
      this.revision++;
    },
    medicalRecordNumberFilter() {
      this.revision++;
    },
    tissueFilter() {
      this.revision++;
    },
    specimenFilter() {
      this.revision++;
    },
    selectedStatuses: {
      handler() {
        this.revision++;
      },
      deep: true
    }
  },
  methods: {
    async fetchOntologies() {
      let ontologies = (await OntologyCollection.fetchAll({ light: true })).array;
      ontologies.sort((a, b) => a.name.localeCompare(b.name));
      this.ontologies = ontologies;
    },
    async fetchMaxFilters() {
      let stats = await ProjectCollection.fetchBounds({ withMembersCount: true });

      this.maxNbMembers = Math.max(10, stats.members.max);
      this.maxNbImages = Math.max(10, stats.numberOfImages.max);
      this.maxNbUserAnnotations = Math.max(100, stats.numberOfAnnotations.max);
      this.maxNbReviewedAnnotations = Math.max(100, stats.numberOfReviewedAnnotations.max);
    },
    async fetchTags() {
      this.availableTags = [{ id: 'null', name: this.$t('no-tag') }, ...(await TagCollection.fetchAll()).array];
    },
    async fetchAIRunners() {
      // 导入AIRunner
      this.aiRunners = await AIRunner.fetchAll();
    },

    async fetchAllUsers() {
      try {
        this.allUsers = (await UserCollection.fetchAll()).array;
      } catch (error) {
        console.error('Error fetching users:', error);
        this.$notify({ type: 'error', text: this.$t('failed-to-fetch-users') });
      }
    },

    hydrateProjectRows(projects = []) {
      projects.forEach(project => {
        this.fetchProjectThumbnail(project);
        this.fetchProjectAIStatus(project);
      });
    },

    projectThumbnail(project) {
      return this.projectThumbnails[project.id] || null;
    },

    projectThumbnailLoading(project) {
      return Boolean(this.projectThumbnailRequests[project.id]);
    },

    async fetchProjectThumbnail(project) {
      if (!project || !project.id || this.projectThumbnailRequests[project.id] ||
          Object.prototype.hasOwnProperty.call(this.projectThumbnails, project.id)) {
        return;
      }

      this.$set(this.projectThumbnailRequests, project.id, true);
      try {
        const imageCollection = new ImageInstanceCollection({
          filterKey: 'project',
          filterValue: project.id,
          sort: 'id',
          order: 'asc',
          max: 1
        });

        const images = await imageCollection.fetchPage(0);
        this.$set(this.projectThumbnails, project.id, images.array[0] || null);
      } catch (error) {
        console.error('Error fetching project thumbnail:', error);
        this.$set(this.projectThumbnails, project.id, null);
      } finally {
        this.$delete(this.projectThumbnailRequests, project.id);
      }
    },

    projectViewerLink(project) {
      const thumbnail = this.projectThumbnail(project);
      if (!thumbnail) {
        return `/project/${project.id}`;
      }

      const path = `/project/${project.id}/image/${thumbnail.id}`;
      if (this.$keycloak.hasTemporaryToken) {
        return { path, query: { access_token: this.$keycloak.temporaryToken } };
      }
      return path;
    },

    projectAIStatus(project) {
      return this.aiStatuses[project.id] || null;
    },

    async fetchProjectAIStatus(project) {
      if (!project || !project.id || this.aiStatusRequests[project.id]) {
        return;
      }

      this.$set(this.aiStatusRequests, project.id, true);
      try {
        const response = await Cytomine.instance.api.get(`ai-algorithm-jobs?projectId=${project.id}`);
        let jobs = response.data || [];
        if (jobs.collection) {
          jobs = response.data.collection;
        } else if (!Array.isArray(jobs)) {
          jobs = [jobs];
        }
        this.$set(this.aiStatuses, project.id, latestProjectAIStatus(jobs));
      } catch (error) {
        console.error('Failed to fetch jobs for project ', project.id, error);
        this.$set(this.aiStatuses, project.id, null);
      } finally {
        this.$delete(this.aiStatusRequests, project.id);
      }
    },

    getAiStatusHint(status) {
      if (!status) return '';
      if (status === 'Process') return this.$t('ai-analyzing-folder');
      if (status === 'Error') return this.$t('ai-analysis-folder-error');
      if (status === 'Ready') return this.$t('ai-analysis-folder-completed');
      return this.$t('ai-status-value', { status });
    },

    formatQcStatus(project) {
      return formatProjectQcStatus(project);
    },

    projectCaseId(project) {
      return getProjectCaseId(project);
    },

    qcStatusClass(project) {
      const status = getProjectQcStatus(project);
      if (!status) {
        return 'qc-status-empty';
      }
      return `qc-status-${String(status).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    },

    toggleFilterDisplay() {
      this.filtersOpened = !this.filtersOpened;
    },

    async startEditing(project) {
      // 开始编辑项目代表用户
      this.editingProjectId = project.id;

      // 初始化该项目的代表用户列表
      if (project.currentUserRoles && project.currentUserRoles.representatives) {

        // 获取当前项目的所有代表用户
        const currentRepresentatives = await ProjectRepresentativeCollection.fetchAll({
          filterKey: 'project',
          filterValue: project.id
        });

        const currentRepresentativeUsers = currentRepresentatives.array || [];
        const currentRepresentativeIds = currentRepresentativeUsers.map(rep => rep.user);
        this.$set(this.projectRepresentatives, project.id, currentRepresentativeIds);
      } else {
        this.$set(this.projectRepresentatives, project.id, []);
      }

      console.log('projectRepresentatives:', this.projectRepresentatives);
    },

    cancelEditing() {
      // 取消编辑
      this.editingProjectId = null;
      // this.$delete(this.projectRepresentatives, project.id);
    },

    async saveRepresentatives(project) {
      // 保存项目代表用户
      try {
        // 获取当前项目的所有代表用户
        const currentRepresentatives = await ProjectRepresentativeCollection.fetchAll({
          filterKey: 'project',
          filterValue: project.id
        });

        const currentRepresentativeUsers = currentRepresentatives.array || [];
        const currentRepresentativeIds = currentRepresentativeUsers.map(rep => rep.user);

        // 获取用户选择的代表用户
        const selectedRepresentatives = this.projectRepresentatives[project.id] || [];

        // 找出需要删除的代表用户（在current中但不在selected中的）
        const toRemove = currentRepresentativeIds.filter(
          id => !selectedRepresentatives.includes(id)
        );

        console.log('toRemove:', toRemove);

        // 找出需要添加的代表用户（在selected中但不在current中的）
        const toAdd = selectedRepresentatives.filter(
          id => !currentRepresentativeIds.includes(id)
        );

        console.log('toAdd:', toAdd);

        await project.addUsers(toAdd);

        // 添加新的代表用户
        for (const id of toAdd) {
          const newRep = new ProjectRepresentative({
            project: project.id,
            user: id
          });
          await project.addAdmin(id);
          await newRep.save();
        }

        // 删除不再需要的代表用户
        for (const id of toRemove) {
          await project.deleteAdmin(id);
          await ProjectRepresentative.delete(0, project.id, id);
        }

        // 更新本地数据
        this.revision++;

        // 结束编辑状态
        this.editingProjectId = null;

        this.$notify({
          type: 'success',
          text: this.$t('users-assigned-successfully')
        });
      } catch (error) {
        console.error('Error updating users:', error);
        this.$notify({
          type: 'error',
          text: this.$t('failed-to-update-users')
        });
      }
    },

    updateProject() {
      this.revision++;
    },
    async deleteProject(projectToDelete) {
      try {
        await projectToDelete.delete();
        this.revision++;
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-project-deletion', { projectName: projectToDelete.name })
        });
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-project-deletion', { projectName: projectToDelete.name })
        });
        return;
      }
    },

    updateProject() {
      this.revision++;
    },

    async openProject(project) {
      try {
        // 获取项目的第一张图片
        const imageCollection = new ImageInstanceCollection({
          filterKey: 'project',
          filterValue: project.id,
          sort: 'id',
          order: 'asc',
          max: 1
        });

        const images = await imageCollection.fetchPage(0);
        if (images.array.length > 0) {
          const firstImage = images.array[0];
          if (this.$keycloak.hasTemporaryToken) {
            this.$router.push(`/project/${project.id}/image/${firstImage.id}?access_token=${this.$keycloak.temporaryToken}`);
          }
          else {
            this.$router.push(`/project/${project.id}/image/${firstImage.id}`);
          }

        } else {
          this.$notify({ type: 'error', text: this.$t('case-has-no-images-add-before-opening') });
        }
      } catch (error) {
        å
        console.error('Error fetching first image:', error);
      }
    },
    formatStatus(status, statusCode) {
      return formatProjectReviewStatus(status, statusCode);
    },

    statusClass(status, statusCode) {
      const label = formatProjectReviewStatus(status, statusCode);
      return label !== '-' ? `status-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : '';
    },

    openAddImageModal(project) {
      this.selectedProject = project;
      this.addImageModal = true;
    },

    openShareModal(project) {
      this.selectedProject = project;
      this.shareProjectModal = true;
    },

    updateProject() {
      this.revision++;
    },

    resetFilters() {
      // 重置搜索字符串
      this.searchString = '';

      // 重置选择的过滤器
      this.selectedOntologies = [];
      this.selectedRoles = [];
      this.selectedTags = [];
      this.selectedStatuses = [];

      // 重置滑块过滤器
      this.boundsMembers = [0, this.maxNbMembers];
      this.boundsImages = [0, this.maxNbImages];
      this.boundsUserAnnotations = [0, this.maxNbUserAnnotations];
      this.boundsReviewedAnnotations = [0, this.maxNbReviewedAnnotations];

      // 重置新的文本过滤字段
      this.patientIdFilter = '';
      this.patientNameFilter = '';
      this.accessionIdFilter = '';
      this.medicalRecordNumberFilter = '';
      this.tissueFilter = '';
      this.specimenFilter = '';

      // 触发重新查询
      this.revision++;
    },
    deleteSelectedProjects() {
      this.$buefy.dialog.confirm({
        title: this.$t('delete-multiple-projects'),
        message: this.$t('delete-multiple-projects-confirm-message', { count: this.checkedProjects.length }),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            // 执行批量删除
            const promises = this.checkedProjects.map(project => project.delete());
            await Promise.all(promises);

            // 清空选中项并刷新列表
            this.checkedProjects = [];
            this.bulkActionModal = false;
            this.updateProject();

            this.$notify({
              type: 'success',
              text: this.$t('notif-success-multiple-projects-deletion', { count: this.checkedProjects.length })
            });
          } catch (error) {
            this.$notify({
              type: 'error',
              text: this.$t('notif-error-multiple-projects-deletion')
            });
          }
        }
      });
    },

    bulkShare() {
      // 批量分享功能
      if (this.checkedProjects.length === 0) {
        this.$buefy.toast.open({
          message: this.$t('please-select-at-least-one-project-to-share'),
          type: 'is-warning'
        });
        return;
      }

      // 设置要批量分享的项目
      this.selectedProjects = this.checkedProjects;
      this.shareProjectModal = true;
    },

    bulkAssign() {
      // 批量分配功能
      if (this.checkedProjects.length === 0) {
        this.$buefy.toast.open({
          message: this.$t('please-select-at-least-one-project-to-assign'),
          type: 'is-warning'
        });
        return;
      }

      this.assignToModal = true;
      // 默认清空之前的选择
      this.bulkRepresentatives = [];
    },

    bulkRunAI() {
      // 批量运行AI功能
      if (this.aiRunners.length === 0) {
        this.$buefy.toast.open({
          message: this.$t('no-ai-runners-available'),
          type: 'is-danger'
        });
        return;
      }

      this.aiRunnerSelectionModal = true;
    },

    runAIOnProject(project) {
      // 单个项目运行AI功能
      if (this.aiRunners.length === 0) {
        this.$buefy.toast.open({
          message: this.$t('no-ai-runners-available'),
          type: 'is-danger'
        });
        return;
      }

      this.projectToRunAI = project;
      this.singleAIRunnerSelectionModal = true;
    },

    async confirmBulkAssign() {
      try {
        // 为每个选中的项目设置代表用户
        const assignPromises = this.checkedProjects.map(async (project) => {
          // 获取当前项目的所有代表用户
          const currentRepresentatives = await ProjectRepresentativeCollection.fetchAll({
            filterKey: 'project',
            filterValue: project.id
          });

          const currentRepresentativeUsers = currentRepresentatives.array || [];
          const currentRepresentativeIds = currentRepresentativeUsers.map(rep => rep.user);

          // 找出需要删除的代表用户（在current中但不在bulk中的）
          const toRemove = currentRepresentativeIds.filter(
            id => !this.bulkRepresentatives.includes(id)
          );

          // 找出需要添加的代表用户（在bulk中但不在current中的）
          const toAdd = this.bulkRepresentatives.filter(
            id => !currentRepresentativeIds.includes(id)
          );

          await project.addUsers(toAdd);
          // 添加新的代表用户
          for (const id of toAdd) {
            const newRep = new ProjectRepresentative({
              project: project.id,
              user: id
            });
            await project.addAdmin(id);
            await newRep.save();
          }

          // 删除不再需要的代表用户
          for (const id of toRemove) {
            await project.deleteAdmin(id);
            await ProjectRepresentative.delete(0, project.id, id);
          }
        });

        // 等待所有项目完成代表用户设置
        await Promise.all(assignPromises);

        // 关闭模态框
        this.assignToModal = false;

        this.$notify({
          type: 'success',
          text: this.$t('successfully-assigned-users-to-projects', { count: this.checkedProjects.length })
        });

        // 刷新项目列表
        this.revision++;

        // 清空选择
        this.checkedProjects = [];
        this.bulkRepresentatives = [];
      } catch (error) {
        console.error('Error assigning users:', error);
        this.$notify({
          type: 'error',
          text: this.$t('failed-to-assign-users')
        });
      }
    },

    async confirmRunAI() {
      if (!this.selectedAIRunner) {
        this.$buefy.toast.open({
          message: this.$t('please-select-an-ai-runner'),
          type: 'is-danger'
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: this.$t('confirm-run-ai-algorithm', { name: this.selectedAIRunner.name }),
        message: this.$t('ai-run-background-message'),
        type: 'is-primary',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            // 关闭模态框
            this.aiRunnerSelectionModal = false;
            this.bulkActionModal = false;

            // 为每个选中的项目运行AI算法
            const runPromises = this.checkedProjects.map(async (project) => {
              const requestData = {
                airunnerId: this.selectedAIRunner.id,
                projectId: project.id
              };

              // 调用API运行AI算法
              await AIAlgorithmJob.runAlgorithm(requestData);
              this.$set(this.aiStatuses, project.id, 'Process');
            });

            // 等待所有项目开始运行AI算法
            await Promise.all(runPromises);

            this.$buefy.toast.open({
              message: this.$t('bulk-ai-processing-started'),
              type: 'is-success'
            });

            console.log('Started AI processing on projects:', this.checkedProjects, 'with runner:', this.selectedAIRunner);

            // 清空选择
            this.checkedProjects = [];
            this.selectedAIRunner = null;
          } catch (error) {
            this.$buefy.toast.open({
              message: this.$t('failed-to-run-ai-algorithm'),
              type: 'is-danger'
            });
            console.error('AI processing failed:', error);
          }
        }
      });
    },

    async confirmSingleRunAI() {
      if (!this.selectedSingleAIRunner) {
        this.$buefy.toast.open({
          message: this.$t('please-select-an-ai-runner'),
          type: 'is-danger'
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: this.$t('confirm-run-ai-algorithm', { name: this.selectedSingleAIRunner.name }),
        message: this.$t('ai-run-background-message'),
        type: 'is-primary',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            // 关闭模态框
            this.singleAIRunnerSelectionModal = false;

            // 为单个项目运行AI算法
            const requestData = {
              airunnerId: this.selectedSingleAIRunner.id,
              projectId: this.projectToRunAI.id
            };

            // 调用API运行AI算法
            await AIAlgorithmJob.runAlgorithm(requestData);
            this.$set(this.aiStatuses, this.projectToRunAI.id, 'Process');

            this.$buefy.toast.open({
              message: this.$t('single-ai-processing-started'),
              type: 'is-success'
            });

            console.log('Started AI processing on project:', this.projectToRunAI, 'with runner:', this.selectedSingleAIRunner);

            // 清空选择
            this.projectToRunAI = null;
            this.selectedSingleAIRunner = null;
          } catch (error) {
            this.$buefy.toast.open({
              message: this.$t('failed-to-run-ai-algorithm'),
              type: 'is-danger'
            });
            console.error('AI processing failed:', error);
          }
        }
      });
    },
  },
  async created() {
    try {
      await Promise.all([
        this.fetchOntologies(),
        this.fetchMaxFilters(),
        this.fetchTags(),
        this.fetchAIRunners(),
        this.fetchAllUsers()
      ]);
    } catch (error) {
      console.log(error);
      this.error = true;
    }
    if (this.$route.query.tags) {
      let queriedTags = this.availableTags.filter(tag => this.$route.query.tags.split(',').includes(tag.name));
      if (queriedTags) {
        this.selectedTags = queriedTags;
      }
    }

    this.loading = false;
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.panel-heading-buttons {
  display: flex;
  padding-right: 0.5rem;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0;
}

.panel-heading-title {
  font-size: 1.2em;
  color: $dark-text-primary;
}

.buttons:not(:last-child){
  margin-bottom: 0;
}

.search-block {
  display: flex;
}

.case-thumbnail-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 52px;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid rgba(190, 214, 238, 0.16);
  background: rgba(222, 238, 255, 0.04);

  ::v-deep .image-thumbnail {
    max-width: 72px;
    max-height: 52px;
    object-fit: cover;
    display: block;
  }
}

.case-thumbnail-placeholder {
  width: 72px;
  height: 52px;
  color: $dark-text-disabled;
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 6px;
  background: rgba(222, 238, 255, 0.035);
}

.case-id {
  font-weight: 600;
  color: $dark-text-primary;
}

.ai-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: help;
}

.case-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 74px;
  min-height: 26px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 700;
  line-height: 1.2;
}

.status-pending {
  background-color: $dark-button-danger-bg;
  color: white;
}

.status-reviewed {
  background-color: rgba($warning, 0.16);
  border: 1px solid rgba($warning, 0.38);
  color: #f0cf7a;
}

.status-approved {
  background-color: rgba($success, 0.14);
  border: 1px solid rgba($success, 0.36);
  color: $success;
}

.qc-status-empty {
  color: $dark-text-disabled;
  border: 1px solid rgba(190, 214, 238, 0.12);
  background: rgba(222, 238, 255, 0.035);
}

.qc-status-pending {
  background-color: $dark-button-danger-bg;
  color: white;
}

.qc-status-reviewed {
  color: #f0cf7a;
  border: 1px solid rgba($warning, 0.38);
  background: rgba($warning, 0.16);
}

.qc-status-approved {
  color: $success;
  border: 1px solid rgba($success, 0.36);
  background: rgba($success, 0.14);
}

.buttons {
  display: flex;
  justify-content: center;
  margin-right: 5px;
}

.bulk-actions {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  margin-right: 5px;

  .button {
    flex: 1;
    min-width: 120px;
    margin-right: 5px;
  }
}

.table-projects {
  margin-top: 1rem;
}
</style>
